import { HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InteractionStatus } from '@azure/msal-browser';
import jwtDecode from 'jwt-decode';
import { duration, Moment } from 'moment';
import moment from 'moment/moment';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { FetchStrategy, RecurringTaskRunnerFactory } from '@nimbus-library/features/recurring-task';
import { IAuthenticationStorage } from '@nimbus-library/features/authentication/services/i-authentication-storage';
import { LogoutRequest } from '@nimbus-library/features/authentication/models/authentication-request.model';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';

export interface IUserInfo {
  userId: string;
  tenantId: string;
  userName: string;
  name: string;
}

export interface IApiToken {
  resource: string;
  token: string;
  permissions: string[];
}

export interface IGraphToken {
  aud: string;
  iss: string;
  iat: number;
  nbf: number;
  exp: number;
  acct: number;
  acr: string;
  aio: string;
  preferred_username?: string;
  amr: Array<string>;
  app_displayname: string;
  appid: string;
  appidacr: string;
  family_name: string;
  given_name: string;
  ipaddr: string;
  name: string;
  oid: string;
  platf: string;
  puid: string;
  scp: string;
  sub: string;
  tid: string;
  unique_name: string;
  upn: string;
  uti: string;
  ver: string;
  xms_tcdt: number;
}

@Injectable()
export abstract class IAuthenticationService {
  public isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(undefined);
  public logout$: Subject<void> = new Subject<void>();
  public abstract isAuthenticated: boolean;
  protected readonly environmentConfiguration: any;

  public abstract status$: Observable<InteractionStatus>;

  protected readonly defaultScopes = ['user.read', 'openid', 'profile'];

  protected constructor(
    protected readonly recurringTaskRunnerFactory: RecurringTaskRunnerFactory,
    protected readonly authenticationStorage: IAuthenticationStorage,
  ) {
    this.environmentConfiguration = (window as any)['EnvironmentConfiguration'];

    this.isAuthenticated$
      .pipe(
        skipWhile((isAuthenticated) => isAuthenticated === undefined),
        distinctUntilChanged(),
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            this.authenticationStorage.clearAuthData();
            this.logout$.next();
            return EMPTY;
          }
          return this.getTokenRenewalTimer();
        }),
      )
      .subscribe();
  }

  public abstract getUserInfo(): IUserInfo;

  public abstract trackAuthentication(): Observable<void>;

  public abstract getApiToken$(forceRefresh: boolean, scopes: string[]): Observable<IApiToken>;

  public abstract getRawApiToken$(forceRefresh: boolean, scopes: string[]): Observable<any>;

  public abstract grantExplicitConsent$(scopes: string[], redirectUrl: string);

  public abstract login(): Observable<void>;

  public abstract sso(loginHint: string): Observable<boolean>;

  public abstract logout(destroyServerSession: boolean, logoutRequest: LogoutRequest, reload: boolean): void;

  public abstract aquireIdToken$(loginHint?: string): Observable<{ token: string; expiration: Moment }>;

  public onUnauthorized(_request: HttpRequest<unknown>): void {
    this.logout(
      false,
      {
        autoLogin: false,
        returnUrl: this.getReturnUrl(),
      },
      true,
    );
  }

  public silentLogin(): Observable<boolean> {
    if (this.authenticationStorage?.getTeamsInfo()) {
      return this.sso(this.authenticationStorage?.getTeamsInfo()).pipe(catchError(() => this.silentLoginByAquireIdToken()));
    }

    return this.silentLoginByAquireIdToken();
  }

  private silentLoginByAquireIdToken() {
    this.authenticationStorage.saveTeamsInfo(null, moment().add(1, 'year'));
    return this.aquireIdToken$().pipe(
      take(1),
      filter((response) => !!response),
      map((result) => {
        this.authenticationStorage.saveAuthData(result.token, this.getUserInfo(), result.expiration);
        this.isAuthenticated$.next(true);

        return true;
      }),
      catchError(() => of(false)),
    );
  }

  public grantAdminConsent(scopes: string[], redirectUrl: string) {
    window.open(this.createAdminConsentLink(scopes, redirectUrl), '_blank');
  }

  public createAdminConsentLink(scopes: string[], redirectUrl: string, tenantId?: string, clientId?: string): string {
    const scopeSet = scopes.join(' ');

    const url = `https://login.microsoftonline.com/${tenantId || this.getUserInfo().tenantId}/v2.0/adminconsent?client_id=${
      clientId || this.environmentConfiguration.azureAdAppId
    }&state=12345&redirect_uri=${encodeURIComponent(redirectUrl)}&scope=${encodeURIComponent(scopeSet)}`;

    return url;
  }

  public createAdminConsentMessagingLink(scopes: string[], redirectUrl: string, tenantId?: string): string {
    return this.createAdminConsentLink(scopes, redirectUrl, tenantId, this.environmentConfiguration.messagingGraphPrincipalId);
  }
  protected extractPermissions(token: string): string[] {
    const tokenObj = this.decodeToken(token);

    return tokenObj.scp ? tokenObj.scp.split(' ').map((permName) => permName.toLowerCase()) : [];
  }

  protected decodeToken(token: string): IGraphToken {
    return jwtDecode<IGraphToken>(token);
  }

  protected processToken(token: string, resource: string): IApiToken {
    if (!token) {
      return null;
    }
    const permissions = this.extractPermissions(token);

    return {
      token,
      permissions,
      resource,
    } as IApiToken;
  }

  public reload(extraParameters?: IMap<string>): void {
    let url = window.location.href;

    if (extraParameters) {
      const params = Object.keys(extraParameters)
        .map((key) => `${key}=${encodeURIComponent(extraParameters[key])}`)
        .filter((param) => !url.includes(param));
      const queryParams = params.join('&');
      url = `${url}${url.includes('?') ? '&' : '?'}${queryParams}`;
    }

    window.location.href = url;
  }

  private getTokenRenewalTimer() {
    return this.recurringTaskRunnerFactory
      .createRunner(() => this.renewToken(), { strategy: FetchStrategy.BackgroundFetch })
      .start(undefined, duration('10', 'minutes'), duration('10', 'minutes'));
  }

  public renewToken(): Observable<{ token: string; expiration: Moment }> {
    return this.aquireIdToken$().pipe(
      take(1),
      filter((response) => !!response),
      tap((response) => {
        this.authenticationStorage.saveAuthData(response.token, this.getUserInfo(), response.expiration);
      }),
    );
  }

  public getReturnUrl() {
    const location = window.location;
    const params = new Proxy(new URLSearchParams(location.search), {
      get: (searchParams, prop) => searchParams.get(prop as string),
    });

    if (params['returnUrl']) {
      return params['returnUrl'];
    }

    return location.pathname.toLowerCase().startsWith('/authentication') ? '' : `${location.pathname}${location.search}`;
  }

  public runningInTeams(): boolean {
    if (
      (window.parent === window.self && (window as any).nativeInterface) ||
      window.name === 'embedded-page-container' ||
      window.name === 'extension-tab-frame'
    ) {
      return true;
    }
    return false;
  }
}
